import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, ButtonBase, CircularProgress, Grid, IconButton, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import e2iApi from '../services/e2iApi';
import { showSnackbar } from '../store/slices/uiSlice';
import type { E2IConfig, EnquiryWorkspace, RegisterCode } from '../types/e2i';
import { ENQUIRY_STATUS, ENQUIRY_TYPES, ESTIMATE_STATUS, JOB_STATUS, QUOTATION_STATUS } from '../types/e2i';
import { errorText, formatAmount, partyLabel, quotationLabel, round2 } from '../utils/e2i';
import CirTab from '../components/e2i/CirTab';
import EnquiryTab from '../components/e2i/EnquiryTab';
import EstimateTab from '../components/e2i/EstimateTab';
import HistoryList from '../components/e2i/HistoryList';
import JobTab from '../components/e2i/JobTab';
import QuotationTab from '../components/e2i/QuotationTab';
import RegisterDialog from '../components/e2i/RegisterDialog';
import { RegisterChip, StatusChip } from '../components/e2i/StatusChip';
import { EnquiryFormState, emptyEnquiryForm, enquiryProblems, formFromEnquiry, inputFromForm } from '../components/e2i/EnquiryForm';
import {
  Notify,
  WORKSPACE_TABS,
  WorkspaceTab,
  WorkspaceTabProps,
  fallbackConfig,
  latestEstimate,
  latestQuotation,
  liveJobs,
} from '../components/e2i/workspace';

/**
 * One enquiry and everything made from it (/e2i/enquiries/:id): the estimate, the quotation revisions, the job
 * and its SAP sales order, the client invoice requests and the history. Approvers open it from their e-mail without
 * the module; the server decides what each user may see and do (permissions).
 */

const TAB_LABELS: Record<WorkspaceTab, string> = {
  enquiry: 'Enquiry',
  estimate: 'Estimate',
  quotation: 'Quotation',
  job: 'Job',
  cirs: 'Invoice requests',
  history: 'History',
};

/** A step of the summary strip: opens its tab. */
const Step: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode; onClick: () => void }> = ({ label, value, hint, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 1, p: 1, '&:hover': { bgcolor: '#f5f7f8' }, '&.Mui-focusVisible': { outline: '2px solid #354a5f' } }}
  >
    {/* Spans: a button holds phrasing content only. */}
    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>{label}</Typography>
    <Box component="span" sx={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{value}</Box>
    {hint ? <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>{hint}</Typography> : null}
  </ButtonBase>
);

const E2IEnquiry: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const enquiryId = Number(id);

  const [ws, setWs] = useState<EnquiryWorkspace | null>(null);
  const [config, setConfig] = useState<E2IConfig | null>(null);
  const [configMissing, setConfigMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<EnquiryFormState>(emptyEnquiryForm);
  const [formDirty, setFormDirtyState] = useState(false);
  const formDirtyRef = useRef(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Counts the edits, so a save that answers after more typing does not drop that typing.
  const formVersion = useRef(0);
  const setFormDirty = (v: boolean) => { if (v) formVersion.current += 1; formDirtyRef.current = v; setFormDirtyState(v); };

  const tabParam = searchParams.get('tab') as WorkspaceTab | null;
  const tab: WorkspaceTab = tabParam && WORKSPACE_TABS.includes(tabParam) ? tabParam : 'enquiry';
  const goTo = useCallback((t: WorkspaceTab) => {
    setSearchParams(t === 'enquiry' ? {} : { tab: t }, { replace: true });
  }, [setSearchParams]);

  const notify: Notify = useCallback((message, severity = 'success') => {
    dispatch(showSnackbar({ message, severity }));
  }, [dispatch]);

  /** Shows a workspace; the enquiry's fields only when the user has no unsaved change there. */
  const applyWs = useCallback((next: EnquiryWorkspace) => {
    setWs(next);
    if (!formDirtyRef.current) setForm(formFromEnquiry(next.enquiry));
  }, []);

  useEffect(() => {
    if (!Number.isInteger(enquiryId) || enquiryId <= 0) { setLoadError('This enquiry does not exist.'); return undefined; }
    let cancelled = false;
    setLoadError(null);
    setWs(null);
    (async () => {
      try {
        const w = await e2iApi.workspace(enquiryId);
        if (cancelled) return;
        formDirtyRef.current = false;
        setFormDirtyState(false);
        applyWs(w);
        try {
          const c = await e2iApi.config(w.enquiry.companyId);
          if (!cancelled) { setConfig(c); setConfigMissing(false); }
        } catch {
          // E.g. an approver opening the link without the module: the workspace still shows.
          if (!cancelled) { setConfig(fallbackConfig(w.enquiry.companyId)); setConfigMissing(true); }
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(errorText(e, 'The enquiry could not be loaded'));
      }
    })();
    return () => { cancelled = true; };
  }, [enquiryId, applyWs]);

  const reload = useCallback(async () => {
    try {
      applyWs(await e2iApi.workspace(enquiryId));
    } catch (e: any) {
      notify(errorText(e, 'The enquiry could not be read again'), 'error');
    }
  }, [enquiryId, applyWs, notify]);

  // While PRPO is creating an SAP document, refresh every 5 seconds.
  const posting = !!ws && ((ws.quotations || []).some((q) => q.sap?.status === 'Posting') || (ws.jobs || []).some((j) => j.sap?.status === 'Posting'));
  useEffect(() => {
    if (!posting) return undefined;
    const timer = setInterval(() => { e2iApi.workspace(enquiryId).then(applyWs).catch(() => undefined); }, 5000);
    return () => clearInterval(timer);
  }, [posting, enquiryId, applyWs]);

  const refresh = async () => {
    setRefreshing(true);
    await reload();
    setRefreshing(false);
  };

  const changeForm = (next: EnquiryFormState) => { setForm(next); setFormDirty(true); };

  const saveEnquiry = async (): Promise<void> => {
    if (!ws) return;
    const problems = enquiryProblems(form, false);
    if (problems.length) throw new Error(`${problems.join('; ')}.`);
    const version = formVersion.current;
    const next = await e2iApi.saveEnquiry(ws.enquiry.enquiryId, ws.enquiry.companyId, inputFromForm(form));
    // What was typed while saving stays on screen, still unsaved.
    if (formVersion.current === version) setFormDirty(false);
    applyWs(next);
  };

  const onSave = async () => {
    setBusy('save');
    setPageError(null);
    try {
      await saveEnquiry();
      notify('Enquiry saved', 'success');
    } catch (e: any) {
      setPageError(errorText(e, 'The enquiry could not be saved'));
    } finally {
      setBusy(null);
    }
  };

  const onPost = async () => {
    if (!ws) return;
    setBusy('post');
    setPageError(null);
    try {
      const problems = enquiryProblems(form, true);
      if (problems.length) throw new Error(`${problems.join('; ')}.`);
      if (formDirtyRef.current) await saveEnquiry();
      const next = await e2iApi.postEnquiry(ws.enquiry.enquiryId);
      setFormDirty(false);
      applyWs(next);
      notify(`${next.enquiry.enquiryNo || 'The enquiry'} is posted (register status 10): the estimate can be prepared`, 'success');
    } catch (e: any) {
      setPageError(errorText(e, 'The enquiry could not be posted'));
    } finally {
      setBusy(null);
    }
  };

  const setRegister = async (code: RegisterCode, note: string) => {
    if (!ws) return;
    const next = await e2iApi.setRegister(ws.enquiry.enquiryId, code, note);
    applyWs(next);
    setRegisterOpen(false);
    notify('Register status set', 'success');
  };

  const summary = useMemo(() => {
    if (!ws) return null;
    const est = latestEstimate(ws);
    const q = latestQuotation(ws);
    const jobs = liveJobs(ws);
    const job = [...jobs].sort((a, b) => b.jobId - a.jobId)[0] || null;
    const total = round2(jobs.reduce((s, j) => s + (Number(j.total) || 0), 0));
    const invoiced = round2(jobs.reduce((s, j) => s + (Number(j.invoicedTotal) || 0), 0));
    const openCirs = (ws.cirs || []).filter((c) => c.status === 'Open' || c.status === 'Submitted' || c.status === 'Booked').length;
    return { est, q, job, jobsCount: jobs.length, total, invoiced, openCirs };
  }, [ws]);

  if (loadError) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate('/e2i/enquiries')}>Enquiries</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{loadError}</Alert>
      </Box>
    );
  }
  if (!ws || !config || !summary) {
    return <Box sx={{ p: 3, display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={20} /><Typography>Loading…</Typography></Box>;
  }

  const e = ws.enquiry;
  const perms = ws.permissions;
  const currency = config.currency || '';
  const typeLabel = ENQUIRY_TYPES.find((t) => t.value === e.enquiryType)?.label || e.enquiryType;
  const tabProps: WorkspaceTabProps = { ws, config, companyId: e.companyId, currency, onWorkspace: applyWs, reload, notify, goTo };
  const counts: Partial<Record<WorkspaceTab, number>> = {
    estimate: ws.estimates?.length || 0,
    quotation: ws.quotations?.length || 0,
    job: ws.jobs?.length || 0,
    cirs: ws.cirs?.length || 0,
  };
  const { est, q, job } = summary;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1400 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton aria-label="Back to the enquiries" onClick={() => navigate('/e2i/enquiries')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {e.enquiryNo ? `Enquiry ${e.enquiryNo}` : 'Enquiry (draft)'}
        </Typography>
        <StatusChip status={ENQUIRY_STATUS[e.status]} fallback={e.status} />
        <RegisterChip code={e.registerStatus} name={e.registerName} config={config} />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" size="small" startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={() => { void refresh(); }} disabled={refreshing}>Refresh</Button>
        {perms.canSetRegister ? <Button variant="outlined" size="small" onClick={() => setRegisterOpen(true)}>Register status</Button> : null}
        {e.status === 'Draft' && perms.canPost ? (
          <Button size="small" onClick={() => { void onPost(); }} disabled={busy !== null}>{busy === 'post' ? 'Posting…' : 'Post enquiry'}</Button>
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
        {[partyLabel(e.customer), partyLabel(e.project), typeLabel, e.subject].filter(Boolean).join(' · ')}
      </Typography>

      {config && !config.enabled && !configMissing ? <Alert severity="warning">Enquiry to Invoice is not switched on for this company (Company Management).</Alert> : null}
      {configMissing ? <Alert severity="info">The company&apos;s Enquiry to Invoice settings could not be read; amounts show without the currency.</Alert> : null}
      {pageError ? <Alert severity="error" onClose={() => setPageError(null)} sx={{ whiteSpace: 'pre-line' }}>{pageError}</Alert> : null}
      {(ws.checks || []).map((c, i) => (
        <Alert key={`${c.code}-${i}`} severity={c.level === 'error' ? 'error' : 'warning'}>{c.message}</Alert>
      ))}

      <Paper sx={{ p: 1 }}>
        <Grid container spacing={0.5}>
          <Grid item xs={6} md={3}>
            <Step
              label="Estimate"
              value={est ? <>R{est.revision} <Box component="span" sx={{ fontWeight: 400 }}>· {ESTIMATE_STATUS[est.status]?.label || est.status}</Box></> : 'Not started'}
              hint={est ? `Cost ${formatAmount(est.totalCost)} ${currency}`.trim() : undefined}
              onClick={() => goTo('estimate')}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Step
              label="Quotation"
              value={q ? quotationLabel(q.quotationNo, q.revision) : 'Not prepared'}
              hint={q ? `${QUOTATION_STATUS[q.status]?.label || q.status} · ${formatAmount(q.total)} ${currency}`.trim() : undefined}
              onClick={() => goTo('quotation')}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Step
              label="Job"
              value={job ? job.jobNo || 'Draft job' : 'Not created'}
              hint={job ? `${JOB_STATUS[job.status]?.label || job.status}${job.sap?.docNum ? ` · SO ${job.sap.docNum}` : ''}` : undefined}
              onClick={() => goTo('job')}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Step
              label="Invoiced"
              value={summary.jobsCount ? `${formatAmount(summary.invoiced)} of ${formatAmount(summary.total)} ${currency}`.trim() : '—'}
              hint={summary.openCirs ? `${summary.openCirs} invoice request${summary.openCirs === 1 ? '' : 's'} in progress` : undefined}
              onClick={() => goTo('cirs')}
            />
          </Grid>
        </Grid>
      </Paper>

      <Box>
        <Tabs
          value={tab}
          onChange={(_ev, v) => goTo(v)}
          aria-label="Enquiry sections"
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: '1px solid #e3e8eb', mb: 2 }}
        >
          {WORKSPACE_TABS.map((t) => (
            <Tab
              key={t}
              value={t}
              id={`e2i-tab-${t}`}
              aria-controls={`e2i-tabpanel-${t}`}
              label={counts[t] ? `${TAB_LABELS[t]} (${counts[t]})` : TAB_LABELS[t]}
            />
          ))}
        </Tabs>
        {/* Every tab stays mounted, so unsaved edits survive a look at another tab. */}
        {WORKSPACE_TABS.map((t) => (
          <Box key={t} role="tabpanel" id={`e2i-tabpanel-${t}`} aria-labelledby={`e2i-tab-${t}`} hidden={tab !== t}>
            {t === 'enquiry' ? (
              <EnquiryTab {...tabProps} form={form} onFormChange={changeForm} dirty={formDirty} busy={busy} onSave={onSave} onPost={onPost} />
            ) : null}
            {t === 'estimate' ? <EstimateTab {...tabProps} /> : null}
            {t === 'quotation' ? <QuotationTab {...tabProps} /> : null}
            {t === 'job' ? <JobTab {...tabProps} /> : null}
            {t === 'cirs' ? <CirTab {...tabProps} /> : null}
            {t === 'history' ? (
              <Paper sx={{ p: 2 }}>
                <HistoryList history={ws.history || []} />
              </Paper>
            ) : null}
          </Box>
        ))}
      </Box>

      <RegisterDialog
        open={registerOpen}
        subject={e.enquiryNo || ''}
        current={e.registerStatus}
        config={config}
        onClose={() => setRegisterOpen(false)}
        onSave={setRegister}
      />
    </Box>
  );
};

export default E2IEnquiry;
